import { Chart as ChartJS } from 'chart.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import {
  createSitePerformanceChartJsBarHoverPlugin,
  setSitePerformanceChartJsHoveredSiteIndex,
  SitePerformanceChartHoverState,
} from '../../plugins/sitePerformanceChartJsBarHoverPlugin';
import { SitePerformanceChartTooltipOverlay } from '../SitePerformanceChartTooltip/SitePerformanceChartTooltipOverlay';
import { StyledSitePerformanceChartJsCanvasWrapper } from './SitePerformanceChartJs.styles';
import {
  createSitePerformanceChartJsData,
  createSitePerformanceChartJsOptions,
  registerSitePerformanceChartJsBuiltInElements,
  sitePerformanceChartJsPlugins,
} from './sitePerformanceChartJsChartConfig';
import {
  SitePerformanceChartData,
  SitePerformanceChartDatum,
} from 'app/modules/site-management/widgets/fleet-dashboard/utils/SitePerformanceChartUtils';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import { DATE_FORMAT_PATTERN } from 'lib/utils/date-handling/DateTime.types';

export interface SitePerformanceChartJsChartProps {
  chartPlotHeight: number;
  pageActualData: SitePerformanceChartDatum[];
  pagePlannedData: SitePerformanceChartDatum[];
  isGcd: boolean;
  startDate: string;
  endDate: string;
  chartIndex: number;
  data: SitePerformanceChartData[];
}

export const SitePerformanceChartJsChart = ({
  chartPlotHeight,
  pageActualData,
  pagePlannedData,
  isGcd,
  startDate,
  endDate,
  chartIndex,
  data,
}: SitePerformanceChartJsChartProps): JSX.Element => {
  const { t, i18n } = useTranslation();

  const [hoverState, setHoverState] = useState<SitePerformanceChartHoverState>({
    siteIndex: null,
    seriesIndex: null,
  });
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [chartContainerSize, setChartContainerSize] = useState({ width: 0, height: 0 });
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ChartJS<'bar'>>(null);
  const hoverStateRef = useRef<SitePerformanceChartHoverState>(hoverState);

  const handleHoverChange = useCallback((nextHoverState: SitePerformanceChartHoverState): void => {
    const currentHoverState = hoverStateRef.current;

    if (
      currentHoverState.siteIndex === nextHoverState.siteIndex &&
      currentHoverState.seriesIndex === nextHoverState.seriesIndex
    ) {
      return;
    }

    hoverStateRef.current = nextHoverState;
    setHoverState(nextHoverState);
  }, []);

  const barHoverPlugin = useMemo(
    () => createSitePerformanceChartJsBarHoverPlugin(handleHoverChange),
    [handleHoverChange]
  );

  registerSitePerformanceChartJsBuiltInElements();

  const chartPlugins = useMemo(() => [...sitePerformanceChartJsPlugins, barHoverPlugin], [barHoverPlugin]);

  const handleChartMouseMove = useCallback((event: React.MouseEvent<HTMLDivElement>): void => {
    const rect = event.currentTarget.getBoundingClientRect();

    setCursorPosition({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  }, []);

  const handleChartMouseLeave = useCallback((): void => {
    const chart = chartRef.current;

    if (chart) {
      setSitePerformanceChartJsHoveredSiteIndex(chart, null);
    }

    handleHoverChange({ siteIndex: null, seriesIndex: null });
  }, [handleHoverChange]);

  useEffect((): void => {
    handleHoverChange({ siteIndex: null, seriesIndex: null });
  }, [chartIndex, data, handleHoverChange]);

  useEffect((): (() => void) | void => {
    const chartContainer = chartContainerRef.current;

    if (!chartContainer) {
      return undefined;
    }

    const updateChartContainerSize = (): void => {
      setChartContainerSize({
        width: chartContainer.clientWidth,
        height: chartContainer.clientHeight,
      });
    };

    updateChartContainerSize();

    const resizeObserver = new ResizeObserver(updateChartContainerSize);

    resizeObserver.observe(chartContainer);

    return (): void => {
      resizeObserver.disconnect();
    };
  }, [chartPlotHeight]);

  const tooltipSubtitle = useMemo((): string => {
    const formattedStartDate = DateTime.formatDateByLocale(i18n.language, startDate, DATE_FORMAT_PATTERN.DATE);
    const formattedEndDate = DateTime.formatDateByLocale(i18n.language, endDate, DATE_FORMAT_PATTERN.DATE);

    return `${formattedStartDate} — ${formattedEndDate}`;
  }, [endDate, i18n.language, startDate]);

  const chartData = useMemo(
    () =>
      createSitePerformanceChartJsData({
        pageActualData,
        pagePlannedData,
        isGcd,
        hoveredSiteIndex: hoverState.siteIndex,
      }),
    [hoverState.siteIndex, isGcd, pageActualData, pagePlannedData]
  );

  const chartOptions = useMemo(() => createSitePerformanceChartJsOptions(chartData.labels), [chartData.labels]);

  return (
    <div className="site-performance-chart__chart-container">
      <StyledSitePerformanceChartJsCanvasWrapper
        ref={chartContainerRef}
        className="site-performance-chart__chart"
        onMouseMove={handleChartMouseMove}
        onMouseLeave={handleChartMouseLeave}
      >
        <Bar ref={chartRef} data={chartData} options={chartOptions} plugins={chartPlugins} height={chartPlotHeight} />

        {hoverState.siteIndex !== null && hoverState.seriesIndex !== null && (
          <SitePerformanceChartTooltipOverlay
            visible
            cursorX={cursorPosition.x}
            cursorY={cursorPosition.y}
            containerWidth={chartContainerSize.width}
            containerHeight={chartContainerSize.height}
            siteIndex={hoverState.siteIndex}
            focusedSeriesIndex={hoverState.seriesIndex}
            pageActualData={pageActualData}
            pagePlannedData={pagePlannedData}
            isGcd={isGcd}
            subtitle={tooltipSubtitle}
            t={t}
          />
        )}
      </StyledSitePerformanceChartJsCanvasWrapper>
    </div>
  );
};
